import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  ArrowLeft, MapPin, Phone, CheckCircle, XCircle, AlertTriangle,
  Play, Check, Camera, FileText, Plus, Clock, Package
} from 'lucide-react'
import api from '../../services/api'
import { useToast } from '../../components/Toast'
import PhotoUploadButton from '../../components/mobile/PhotoUploadButton'
import WorkOrderSignDialog from '../../components/mobile/WorkOrderSignDialog'
import LeveringsbonSectie from '../../components/mobile/LeveringsbonSectie'
import UrenRegistratieSectie from '../../components/mobile/UrenRegistratieSectie'
import ServiceoverzichtSectie from '../../components/mobile/ServiceoverzichtSectie'

/**
 * Mobile Job Detail Page
 * Complete work order interface with checklist, photos, and signature.
 */
export default function MobileJobDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [showSignDialog, setShowSignDialog] = useState(false)
  const [selectedPhotoType, setSelectedPhotoType] = useState('OPLEVERING')

  // Fetch job detail
  const { data: jobDetail, isLoading } = useQuery({
    queryKey: ['mobile-job-detail', id],
    queryFn: async () => {
      const response = await api.get(`/mobile/worker/jobs/${id}`)
      return response.data
    }
  })

  // Update checklist item mutation
  const updateChecklistMutation = useMutation({
    mutationFn: async ({ itemId, status }) => {
      const formData = new FormData()
      formData.append('status', status)
      
      const response = await api.post(
        `/mobile/worker/jobs/${id}/checklist/${itemId}/update`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mobile-job-detail', id])
      toast.success('Checklist item bijgewerkt')
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Fout bij bijwerken')
    }
  })

  // Start job mutation
  const startJobMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/mobile/worker/jobs/${id}/start`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mobile-job-detail', id])
      queryClient.invalidateQueries(['mobile-worker-jobs'])
      toast.success('Job gestart')
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Fout bij starten')
    }
  })

  // Complete job mutation
  const completeJobMutation = useMutation({
    mutationFn: async ({ actualHours, notes }) => {
      const formData = new FormData()
      if (actualHours) formData.append('actual_hours', actualHours.toString())
      if (notes) formData.append('notes', notes)
      
      const response = await api.post(`/mobile/worker/jobs/${id}/complete`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mobile-job-detail', id])
      queryClient.invalidateQueries(['mobile-worker-jobs'])
      toast.success('Job afgerond')
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Fout bij afronden')
    }
  })

  // Create service job mutation
  const createServiceJobMutation = useMutation({
    mutationFn: async ({ description, photoId }) => {
      const formData = new FormData()
      formData.append('description', description)
      if (photoId) formData.append('photo_id', photoId.toString())
      
      const response = await api.post(`/mobile/worker/jobs/${id}/create-service`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    onSuccess: () => {
      toast.success('Service job aangemaakt')
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Fout bij aanmaken service job')
    }
  })

  // Upload photo mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: async ({ file, photoType, description }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('photo_type', photoType)
      if (description) formData.append('description', description)
      
      const response = await api.post(`/mobile/worker/jobs/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['mobile-job-detail', id])
      toast.success('Foto geüpload')
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Fout bij uploaden foto')
    }
  })

  const handleChecklistUpdate = (itemId, status) => {
    updateChecklistMutation.mutate({ itemId, status })
  }

  const handleStart = () => {
    if (!jobDetail?.can_start) {
      toast.error('Job kan niet gestart worden (productie nog niet klaar)')
      return
    }
    startJobMutation.mutate()
  }

  const handleComplete = () => {
    const hours = prompt('Aantal gewerkte uren:')
    if (hours !== null) {
      const notes = prompt('Opmerkingen (optioneel):')
      completeJobMutation.mutate({
        actualHours: parseFloat(hours) || null,
        notes: notes || null
      })
    }
  }

  const handleCreateService = () => {
    const description = prompt('Beschrijving van het probleem:')
    if (description) {
      createServiceJobMutation.mutate({ description })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-canvas dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    )
  }

  if (!jobDetail) {
    return (
      <div className="min-h-screen bg-canvas dark:bg-gray-900 p-4">
        <div className="r11-empty">
          <FileText className="r11-empty__icon" />
          <p className="r11-empty__title">Job niet gevonden</p>
        </div>
      </div>
    )
  }

  const { job, checklist_items, photos, can_start, can_complete } = jobDetail
  const isLevering = job?.job_type === 'LEVERING'
  const isPlaatsing = job?.job_type === 'PLAATSING' || job?.job_type === 'AFWERKING'

  return (
    <div className="min-h-screen bg-canvas dark:bg-gray-900 pb-24">
      {/* Header */}
      <div className="bg-surface dark:bg-gray-800 border-b border-line dark:border-gray-700 sticky top-0 z-10">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/mobile/worker')}
            className="p-2 text-ink dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-control"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-ink dark:text-white">Werkbon</h1>
            <p className="text-sm text-muted">{job.project_name}</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Job Info */}
        <div className="r11-card p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="font-semibold text-ink dark:text-white">{job.project_name}</h2>
              <p className="text-sm text-muted">{job.job_type}</p>
            </div>
            <span className={`badge ${
              job.status === 'IN_PROGRESS' ? 'badge-warning' :
              job.status === 'COMPLETED' ? 'badge-success' :
              'badge-active'
            }`}>
              {job.status === 'IN_PROGRESS' ? 'In uitvoering' :
               job.status === 'COMPLETED' ? 'Voltooid' : 'Gepland'}
            </span>
          </div>

          {job.address && (
            <div className="flex items-start gap-2 mb-2 text-sm text-muted">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{job.address}</span>
            </div>
          )}

          {(job.contact_name || job.client_name) && (
            <div className="flex items-center gap-2 mb-2 text-sm text-ink dark:text-gray-200">
              <span className="font-medium">{job.contact_name || job.client_name}</span>
              {job.contact_phone && (
                <a href={`tel:${job.contact_phone}`} className="text-accent">
                  <Phone className="w-4 h-4" />
                </a>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4">
            {can_start && (
              <button
                onClick={handleStart}
                className="btn-primary flex-1 px-4 py-3"
              >
                <Play className="w-5 h-5" />
                Start Werkzaamheden
              </button>
            )}
            {can_complete && (
              <button
                onClick={handleComplete}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-control font-semibold text-sm transition-colors hover:bg-green-700"
              >
                <Check className="w-5 h-5" />
                Afronden
              </button>
            )}
          </div>
        </div>

        {/* Checklist */}
        <div className="r11-card p-4">
          <h2 className="text-lg font-semibold text-ink dark:text-white mb-4">Checklijst</h2>
          <div className="space-y-3">
            {checklist_items && checklist_items.length > 0 ? (
              checklist_items.map((item) => (
                <ChecklistItem
                  key={item.id}
                  item={item}
                  onUpdate={(status) => handleChecklistUpdate(item.id, status)}
                />
              ))
            ) : (
              <div className="r11-empty">
                <CheckCircle className="r11-empty__icon" />
                <p className="r11-empty__title">Geen checklist items</p>
              </div>
            )}
          </div>
        </div>

        {/* Photos */}
        <div className="r11-card p-4">
          <h2 className="text-lg font-semibold text-ink dark:text-white mb-4">Foto's</h2>

          {/* Photo Type Selector */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSelectedPhotoType('OPLEVERING')}
              className={`flex-1 px-4 py-3 rounded-control font-semibold text-sm transition-colors ${
                selectedPhotoType === 'OPLEVERING'
                  ? 'bg-accent text-white'
                  : 'bg-gray-100 text-muted dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Oplevering
            </button>
            <button
              onClick={() => setSelectedPhotoType('SCHADEMELDING')}
              className={`flex-1 px-4 py-3 rounded-control font-semibold text-sm transition-colors ${
                selectedPhotoType === 'SCHADEMELDING'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-muted dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              Schademelding
            </button>
          </div>

          {/* Photo Upload */}
          <PhotoUploadButton
            projectId={job.project_id}
            onUploadSuccess={(data) => {
              uploadPhotoMutation.mutate({
                file: data.file,
                photoType: selectedPhotoType,
                description: data.description
              })
            }}
          />

          {/* Photo Gallery */}
          {photos && photos.length > 0 && (
            <div className="grid grid-cols-2 gap-2 mt-4">
              {photos.map((photo, idx) => (
                <div key={idx} className="relative">
                  <img
                    src={photo.url}
                    alt={photo.description || 'Photo'}
                    className="w-full h-32 object-cover rounded-control"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leveringsbon sectie (enkel bij LEVERING jobs) */}
        {isLevering && (
          <LeveringsbonSectie
            projectId={job.project_id}
            jobId={job.job_id}
          />
        )}

        {/* Urenregistratie (alle job types) */}
        <UrenRegistratieSectie
          jobId={job.job_id}
          projectId={job.project_id}
        />

        {/* Serviceoverzicht (PLAATSING, AFWERKING, SERVICE) */}
        {(isPlaatsing || job?.job_type === 'SERVICE') && (
          <ServiceoverzichtSectie projectId={job.project_id} />
        )}

        {/* Service Job Creation (niet bij LEVERING) */}
        {can_complete && !isLevering && (
          <div className="r11-card p-4">
            <h2 className="text-lg font-semibold text-ink dark:text-white mb-4">Service nodig?</h2>
            <button
              onClick={handleCreateService}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-700 rounded-control font-semibold text-sm transition-colors hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
            >
              <Plus className="w-5 h-5" />
              Service Job Aanmaken
            </button>
          </div>
        )}

        {/* Signature (niet bij LEVERING — die heeft eigen ondertekening in LeveringsbonSectie) */}
        {can_complete && !isLevering && (
          <div className="r11-card p-4">
            <h2 className="text-lg font-semibold text-ink dark:text-white mb-4">Handtekening Klant</h2>
            <button
              onClick={() => setShowSignDialog(true)}
              className="btn-primary w-full px-4 py-3"
            >
              <FileText className="w-5 h-5" />
              Laat Klant Ondertekenen
            </button>
          </div>
        )}
      </div>

      {/* Signature Dialog */}
      {showSignDialog && (
        <WorkOrderSignDialog
          isOpen={showSignDialog}
          onClose={() => setShowSignDialog(false)}
          workOrderId={id}
          projectId={job.project_id}
        />
      )}
    </div>
  )
}

// Checklist Item Component
function ChecklistItem({ item, onUpdate }) {
  const [status, setStatus] = useState(item.status || 'PENDING')

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus)
    onUpdate(newStatus)
  }

  return (
    <div className="flex items-center justify-between p-3 border border-line dark:border-gray-700 rounded-control">
      <div className="flex-1">
        <div className="font-medium text-ink dark:text-white">{item.name}</div>
        {item.description && (
          <div className="text-sm text-muted">{item.description}</div>
        )}
        <div className="text-xs text-faint mt-1">
          {item.quantity} {item.unit || 'stuk'}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => handleStatusChange('PLACED')}
          className={`p-3 rounded-control transition-colors ${
            status === 'PLACED'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-gray-100 text-muted dark:bg-gray-700 dark:text-gray-400'
          }`}
          title="Geplaatst"
        >
          <CheckCircle className="w-5 h-5" />
        </button>
        <button
          onClick={() => handleStatusChange('MISSING')}
          className={`p-3 rounded-control transition-colors ${
            status === 'MISSING'
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
              : 'bg-gray-100 text-muted dark:bg-gray-700 dark:text-gray-400'
          }`}
          title="Ontbrekend"
        >
          <AlertTriangle className="w-5 h-5" />
        </button>
        <button
          onClick={() => handleStatusChange('DAMAGED')}
          className={`p-3 rounded-control transition-colors ${
            status === 'DAMAGED'
              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
              : 'bg-gray-100 text-muted dark:bg-gray-700 dark:text-gray-400'
          }`}
          title="Beschadigd"
        >
          <XCircle className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
